import { useProgress } from '../state/ProgressContext';
import { TrainingStepper, type TrainingStepperStep } from '../components/TrainingStepper';
import { isFieldPosition, isHvacPosition, positionForRole } from '../content/positions';
import { HvacTraining } from './HvacTraining';

// The search a rep runs is decided by the product they sell: Find Leads loads their assigned
// product's filters and locks the fields, so there is nothing here to teach about configuring
// them. A field rep still has their own Location step, because where they search means something
// different for them.
const leadGenSearchItems = <>
  <li>The filters are assigned appropriately for you so no further changes to the filters are required.</li>
  <li>Press Search Leads.</li>
  <li>Review the results and pick one strong business before you claim it.</li>
</>;

export function buildRemoteTrainingSteps(assignedServiceName: string | null): TrainingStepperStep[] {
  return [
  {
    key: 'REMOTE_LEAD_IMPORTED',
    title: 'Step 1: Generate and Import a Lead',
    content: <>
      <ul>
        <li>Go to <a href="https://portal.krovance.com" target="_blank" rel="noopener noreferrer">portal.krovance.com</a>.</li>
        <li>Sign in with your Google account. This account is how we identify you.</li>
        <li>Go to the Leads tab.</li>
        <li>Go to the Find Leads tab.</li>
        {leadGenSearchItems}
        <li>Press the checkmark box in the imported column of the lead entry and press import (only claim one for now).</li>
        <li>Krovance provides a lead pool, so a business another representative has already claimed is not available to you. If you see that message, pick a different business.</li>
      </ul>
      <p className="training-step-note">Congratulations—you claimed your first business! This is how you will get your leads from now on: you select and claim them from the pool before visiting.</p>
    </>,
  },
  {
    key: 'REMOTE_VOIP_ISSUED',
    title: 'Step 2: Prepare for a Cold Call',
    content: <ul>
      <li>Go to the Calls tab.</li>
      <li>Request VoIP.ms Registration.</li>
      <li>Once your phone is set up, review the information for the service you will be selling.</li>
      <li>Go to the <strong>Materials</strong> tab (it opens on <strong>{assignedServiceName ?? 'your service'}</strong>) and review the approved service information before making the call.</li>
      <li>Read the call script: your workspace (<a href="https://portal.krovance.com" target="_blank" rel="noopener noreferrer">portal.krovance.com</a>) → <strong>Materials</strong> tab → <strong>AI Receptionist Call Template</strong> → <strong>Read document</strong>. This is what you say on the call.</li>
    </ul>,
  },
  {
    key: 'REMOTE_CALL_LOGGED',
    title: 'Step 3: Make Your First Call',
    content: <ul>
      <li>Open the call script first: your workspace (<a href="https://portal.krovance.com" target="_blank" rel="noopener noreferrer">portal.krovance.com</a>) → <strong>Materials</strong> tab → <strong>AI Receptionist Call Template</strong> → <strong>Read document</strong>. Keep it open and follow it on the call.</li>
      <li><strong>For your first few calls, do not improvise: follow the script exactly, word for word.</strong></li>
      <li>Copy the lead's phone number from your leads sheet.</li>
      <li>Use the VoIP softphone app to make the call. Follow the instructions popup from pressing the call icon next to the number.</li>
      <li><strong>Other ways to reach them are fine</strong>: to secure a lead, you may take the time to reach out in other ways if cold calling isn&apos;t working well or the business can&apos;t answer the phone at that time. As a contractor, you are not restricted to any specific outreach method.</li>
      <li>Once completed, press sync. This step will be completed once sync was successful.</li>
    </ul>,
  },
  {
    key: 'REMOTE_NOTES_COMPLETED',
    title: 'Step 4: Add Notes',
    content: <ul>
      <li>Open the lead you used for the call.</li>
      <li>Select Add Notes.</li>
      <li>Complete the required post-call notes/debrief.</li>
      <li>Submit the notes successfully.</li>
      <li>Then press <strong>Feedback</strong> on the lead and fill out your <strong>Performance Form</strong> about what happened - it opens the Feedback chat with your admin. You can change it later: press <strong>Performance Form</strong> at the top of the Feedback chat.</li>
    </ul>,
  },
  {
    key: 'REMOTE_EMAIL_CONSTRUCTOR_USED',
    title: 'Step 5: Follow Up',
    content: <ul>
      <li>Go to the Email tab.</li>
      <li>Follow-up is by email.</li>
      <li>Select your lead from the list.</li>
      <li>Use the follow-up template: open the <strong>Templates</strong> dropdown and choose <strong>Follow Up Template</strong>. It fills in the subject and body - fill in anything in [brackets], check it, then press Send Email.</li>
      <li>This step will be completed once the email was sent successfully.</li>
    </ul>,
  },
  ];
}

export const fieldTrainingSteps: TrainingStepperStep[] = [
  {
    key: 'FIELD_LEAD_IMPORTED',
    title: 'Step 1: Claim a Business from the Lead Pool',
    content: <>
      <ul>
        <li>Go to <a href="https://portal.krovance.com" target="_blank" rel="noopener noreferrer">portal.krovance.com</a>.</li>
        <li>Sign in with your Google account. This account is how we identify you.</li>
        <li>Go to the Leads tab.</li>
        <li>Go to the Find Leads tab.</li>
        <li>Set the Location to the area where you are currently conducting field sales, so every result is a business you can realistically visit.</li>
        {leadGenSearchItems}
        <li>Firstly, make sure the business is open. You can check their available Google information.</li>
        <li>Ensure it's within the time frame you are okay with. Find one close enough to you.</li>
        <li>Press the checkmark box in the imported column of the lead entry and press import (only claim one for now).</li>
        <li>Krovance provides a lead pool, so a business another representative has already claimed is not available to you. If you see that message, pick a different business.</li>
      </ul>
      <p className="training-step-note">Congratulations—you claimed your first business! This is how you will get your leads from now on: you select and claim them from the pool before visiting.</p>
    </>,
  },
  {
    key: 'FIELD_LEAD_VISITED',
    title: 'Step 2: Visit the Business',
    content: <ul>
      <li>Take a trip to the business you claimed.</li>
      <li>Press the button next to the address to open the visit guide. It has your opener, the discovery questions, the approved objection responses, the QR code, and the leave-behind flyer.</li>
      <li>Ask for the owner, manager, or decision-maker. Ask the discovery questions before you explain the product.</li>
      <li>Show them the product page or QR code, and offer the live demo: they can call <strong>+1 (862) 308-2615</strong> and hear the AI Receptionist answer.</li>
      <li>Get their email address before you leave — the follow-up is an email.</li>
      <li>Once you've visited, mark the lead as Visited on the portal.</li>
    </ul>,
  },
  {
    key: 'FIELD_NOTES_COMPLETED',
    title: 'Step 3: Add Notes and Record the Visit Outcome',
    content: <ul>
      <li>Open the visited lead.</li>
      <li>Select Add Notes.</li>
      <li>Complete the required visit notes/debrief.</li>
      <li>Submit the notes successfully.</li>
      <li>Then press <strong>Record visit outcome</strong> on the same lead and fill in what happened: whether you reached the decision-maker, why they did or did not buy, what they already use, and whether a follow-up is needed. This is what tells Krovance which businesses to send you to next.</li>
      <li>If you set a follow-up, its date shows on the lead in <strong>Leads</strong>.</li>
      <li>Then press <strong>Feedback</strong> on the lead and fill out your <strong>Performance Form</strong> about what happened - it opens the Feedback chat with your admin. You can change it later: press <strong>Performance Form</strong> at the top of the Feedback chat.</li>
    </ul>,
  },
  {
    key: 'FIELD_EMAIL_CONSTRUCTOR_USED',
    title: 'Step 4: Follow Up',
    // Same step, and the same completion trigger, as the remote role's Follow Up above:
    // sending the email fires EMAIL_CONSTRUCTOR_USED from the Portal, which is what marks
    // this step done - there is nothing to submit here.
    content: <ul>
      <li>Go to the Email tab.</li>
      <li>Select your lead from the list. It unlocks once that lead's notes are complete.</li>
      <li>If the lead has no email address yet, add the one you collected during the visit first.</li>
      <li>Use the follow-up template: open the <strong>Templates</strong> dropdown and choose <strong>Follow Up Template</strong>. It fills in the subject and body - fill in anything in [brackets], check it, then press Send Email.</li>
      <li>This step will be completed once the email was sent successfully.</li>
    </ul>,
  },
];

export function FirstLeadTraining() {
  const { progress } = useProgress();
  if (progress && isHvacPosition(progress.roleId)) return <HvacTraining workMode={isFieldPosition(progress.roleId) ? 'FIELD' : 'REMOTE'} />;
  return <AiReceptionistTraining />;
}

function AiReceptionistTraining() {
  const { progress } = useProgress();
  const isFieldSales = isFieldPosition(progress?.roleId);
  const steps = isFieldSales ? fieldTrainingSteps : buildRemoteTrainingSteps(progress?.assignedServiceName ?? null);
  const completedStepKeys = progress?.training.completedStepKeys ?? [];
  const currentStepKey = progress?.training.currentStepKey ?? null;
  const rankTitle = positionForRole(progress?.roleId).listing.title;

  if (!progress) return null;

  return <div className="first-lead-training">
    <h1 className="step-title">Your First Lead: Step-by-Step Training</h1>
    <p className="step-subtitle">Complete each step in order. This page updates automatically as the Portal confirms your actions — no need to submit anything for review.</p>
    {progress.training.approvedAt && <div className="alert success">
      <strong>Your training has been approved!</strong> You've been ranked as a <strong>{rankTitle}</strong> and are ready to begin your day-to-day operations with Krovance.
      <p><a className="btn-primary" href="https://portal.krovance.com">Continue to portal.krovance.com</a></p>
    </div>}
    {progress.training.submittedAt && !progress.training.approvedAt && <div className="alert info"><strong>Submitted</strong> — your training was submitted on {new Date(progress.training.submittedAt).toLocaleString()} and is awaiting review. This page checks automatically for approval.</div>}
    {progress.training.changesFeedback && !progress.training.approvedAt && <div className="alert warning"><strong>Changes requested:</strong> {progress.training.changesFeedback}</div>}
    <section className="card">
      <TrainingStepper steps={steps} completedKeys={completedStepKeys} currentKey={currentStepKey} />
    </section>
    <section className="card training-step-card">
      <h2>How you will manage this lead over time...</h2>
      <ul>
        <li>At this point, you have completed this lead and will receive commission payouts when the client closes.</li>
        <li>To increase the chances of a close, you should daily send a follow up email re-addressing their interest.</li>
        <li>Your job with this lead ends when they follow up with an email confirming their interest and are looking to pay or schedule an appointment for more information. We monitor the emails so we will automatically handle it further.</li>
      </ul>
    </section>
  </div>;
}
